import { useEffect, useState, ChangeEvent } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { useLocation, useHistory } from "react-router-dom";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import TermsAndConditionsCheckbox from "@/components/terms-and-conditions-checkbox/TermsAndConditionsCheckbox";
import CheckBullet from "@/components/bullets/CheckBullet";
import { SetPassword } from "@/models/site-credentials/mobile/SetPassword";
import { FormError } from "@/models/site-credentials/common/FormError";
import { useTranslation } from "react-i18next";
import { PasswordRecoverVerifyOtpResponse } from "@/models/site-credentials/mobile/PasswordRecoverVerifyOtpResponse";
import { passwordRecoverService } from "@/services/site-credentials/mobile/PasswordRecoverService";
import {
  verifySpecialChars,
  verifyLowerCase,
  verifyMinChar,
  verifyNumber,
  verifyUpperCase,
} from "../../../utils/passwordRules";
import ErrorMessage from "@/components/error-message/error-message";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import Input from "@/components/ui/Input";

type Form = SetPassword & FormError;

export default function PasswordRecoverSetNewPassword() {
  const history = useHistory();
  const [labelPasswordVisible, setLabelPasswordVisible] =
    useState<boolean>(false);
  const [
    labelPasswordConfirmationVisible,
    setLabelPasswordConfirmationVisible,
  ] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [acceptedTermsConditions, setAcceptedTermsConditions] =
    useState<boolean>(true);
  const [minCharacters, setMinCharacters] = useState<boolean>(false);
  const [containNumber, setContainNumber] = useState<boolean>(false);
  const [containUppercase, setContainUppercase] = useState<boolean>(false);
  const [containLowercase, setContainLowercase] = useState<boolean>(false);
  const [containSpecialChar, setContainSpecialChar] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [isCheckBoxChecked, setCheckBoxChecked] = useState<boolean>(false);

  const { t } = useTranslation();
  const location = useLocation<PasswordRecoverVerifyOtpResponse>();
  const identifier =
    location.state != null ? location.state.properties.identifier : "";

  //const mustAcceptTermsConditions = location.state.properties.mustAcceptTermsConditions;

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Form>({ mode: "onSubmit", reValidateMode: "onSubmit" });
  const handlePasswordChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setPassword(value);
    setValue("password", value);
    checkAndEnableCheckBox(value, passwordConfirmation);
    setLabelPasswordVisible(value.length > 0 ? true : false);
  };
  const handlePasswordConfirmationChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setPasswordConfirmation(value);
    setValue("passwordConfirmation", value);
    checkAndEnableCheckBox(password, value);
    setLabelPasswordConfirmationVisible(value.length > 0 ? true : false);
  };
  const handleAcceptedTermsConditionsChange = (): void => {
    setAcceptedTermsConditions((acceptedTermsConditions) => {
      return !acceptedTermsConditions;
    });

    setValue("acceptedTermsConditions", acceptedTermsConditions);
    setCheckBoxChecked(acceptedTermsConditions);
    checkAndEnableCheckBox(
      password,
      passwordConfirmation,
      acceptedTermsConditions
    );
  };
  const handleBackButton = (): number => {
    const backSteps =
      location.state.properties.previousStep === "validate-otp" ? 2 : 3;
    return backSteps;
  };

  const onSubmit: SubmitHandler<SetPassword> = async (data) => {
    setLoading(true);
    if (password === passwordConfirmation) {
      try {
        const response = await passwordRecoverService.setPassword(
          {
            identifier: identifier,
            password: data.password,
            passwordConfirmation: data.passwordConfirmation,
            acceptedTermsConditions: data.acceptedTermsConditions,
          },
          location.state != null ? location.state.nextStep : ""
        );

        switch (response.kind) {
          case "success":
            history.push(response.value.nextStep.substring(4), {
              ...response.value,
              responseCode: "100001",
            });
            break;
          case "error":
            setError("submitError", {
              message:
                response.code === "300010"
                  ? t("password_definition.password_repeated")
                  : t(response.message),
            });
            break;
          default:
            setError("submitError", { message: t("common.unexpected_error") });
            break;
        }

        setLoading(false);
      } catch (e) {
        setError("submitError", { message: t("common.unexpected_error") });
        setLoading(false);
      }
    } else {
      setError("password", {
        message: t("password_definition.passwords_not_match"),
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    const passwordValidator = (register: UseFormRegister<Form>) =>
      register("password", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^((?=.*\d)(?=.*[A-Za-z])).{6,}$/,
          message: t("password_definition.password_invalid"),
        },
      });
    const passwordConfirmationValidator = (register: UseFormRegister<Form>) =>
      register("passwordConfirmation", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^((?=.*\d)(?=.*[A-Za-z])).{6,}$/,
          message: t("password_definition.password_invalid"),
        },
      });
    const acceptedTermsConditionsValidator = (
      register: UseFormRegister<Form>
    ) =>
      register("acceptedTermsConditions", {
        required: {
          value: true,
          message: "",
        },
      });
    passwordValidator(register);
    passwordConfirmationValidator(register);

    if (location.state.properties.mustAcceptTermsConditions) {
      acceptedTermsConditionsValidator(register);
    }
  }, [location.state.properties.mustAcceptTermsConditions, register, t]);

  useEffect(() => {
    if (!verifyMinChar(password)) setMinCharacters(false);
    else setMinCharacters(true);

    if (verifyNumber(password)) setContainNumber(true);
    else setContainNumber(false);

    if (verifyUpperCase(password)) setContainUppercase(true);
    else setContainUppercase(false);

    if (verifyLowerCase(password)) setContainLowercase(true);
    else setContainLowercase(false);

    if (verifySpecialChars(password)) setContainSpecialChar(true);
    else setContainSpecialChar(false);
  }, [password]);

  function verifyPasswordsMatch(pw: string, pwConfirmation: string) {
    if (pw === pwConfirmation && pw !== "") {
      setPasswordsMatch(true);
      return true;
    }

    setPasswordsMatch(false);
    return false;
  }

  function checkAndEnableCheckBox(
    pw: string,
    pwConfirmation: string,
    isChecked: boolean | null = null
  ) {
    const checked = location.state.properties.mustAcceptTermsConditions
      ? isChecked ?? isCheckBoxChecked
      : true;

    if (
      verifyPasswordsMatch(pw, pwConfirmation) &&
      verifyMinChar(pw) &&
      verifyNumber(pw) &&
      verifyLowerCase(pw) &&
      verifyUpperCase(pw) &&
      verifySpecialChars(pw) &&
      checked
    ) {
      //setButtonEnabled(true);
    } else {
      //setButtonEnabled(false);
    }
  }

  return (
    <>
      <form
        id="SetUserPassword"
        autoComplete="on"
        onSubmit={(e) => {
          clearErrors();
          handleSubmit(onSubmit)(e);
        }}
      >
        <div className="row justify-center">
          <CredentialsHeader
            title={t("password_definition.header_title")}
            subtitle={t("password_definition.header_message")}
            showLoading={loading}
            previousStepBackXTimes={handleBackButton()}
          />
        </div>
        <div className="flex justify-center">
          <div className="input-outline-eye">
            <Input
              labelText={t("password_definition.password_input")}
              isFilled={labelPasswordVisible}
              isError={errors.password ? true : false}
              errorMsg={errors.password ? errors.password.message : ""}
              onChange={handlePasswordChange}
              ariaLabel="Por favor defina uma palavra-passe com pelo menos oito caracteres que contenha pelo menos um número, uma maiúscula e minúscula e um símbolo."
              type="password"
            />
            <Input
              labelText={t("password_definition.password_input")}
              isFilled={labelPasswordConfirmationVisible}
              isError={errors.passwordConfirmation ? true : false}
              ariaLabel="Escreva novamente para confirmar e avançar."
              errorMsg={
                errors.passwordConfirmation
                  ? errors.passwordConfirmation.message
                  : ""
              }
              onChange={handlePasswordConfirmationChange}
              type="password"
            />
          </div>
          {location.state.properties.mustAcceptTermsConditions && (
            <TermsAndConditionsCheckbox
              onClick={handleAcceptedTermsConditionsChange}
              isError={errors.acceptedTermsConditions ? true : false}
            />
          )}
          <div className="my-0 relative mt-4" aria-hidden>
            <div className="flex w-75 m-auto flex-wrap flex-col">
              <div className="flex justify-content-left password_rules_header">
                {t("password_definition.password_validation_header")}
              </div>

              <CheckBullet
                checked={minCharacters}
                text={t(
                  "password_definition.password_validation_length_maximum"
                )}
              />
              <CheckBullet
                checked={containNumber}
                text={t("password_definition.password_validation_number")}
              />
              <CheckBullet
                checked={containUppercase}
                text={t("password_definition.password_validation_uppercase")}
              />
              <CheckBullet
                checked={containLowercase}
                text={t("password_definition.password_validation_lowercase")}
              />
              <CheckBullet
                checked={containSpecialChar}
                text={t("password_definition.password_validation_special_char")}
              />
              <CheckBullet
                checked={passwordsMatch}
                text={t("password_definition.passwords_match")}
              />
            </div>
          </div>
        </div>
        <ErrorMessage error={errors.submitError} />
        <NeedHelpButton />
        <div className="flex flex-col">
          <CredentialsFooter />
        </div>
      </form>
    </>
  );
}
